import moment from 'moment';
// Attaches moment.tz (with the IANA/DST database) to the shared moment singleton so offset-less inputs
// can be anchored to a named zone. moment-timezone data is already in both eval bundles; this
// side-effect import guarantees moment.tz on the eval path regardless of tree-shaking.
import 'moment-timezone';

import {
  CANONICAL_FORMAT,
  DATETIME_FORMATS,
  USER_EDIT_DATE_FORMAT,
  USER_EDIT_DATETIME_FORMAT,
} from 'util/DateConstants';
import {getLocaleInputFormats} from 'util/DateUtil';
import lruMemoize from 'util/lruMemoize';
import {Optional} from 'util/Types';

// The full set of formats we accept when parsing a free-text date/datetime: locale-aware date/datetime
// layouts (month/day order keyed off languageTag) plus the canonical machine formats. Shared so every text
// date parser accepts exactly the same inputs.
function localeDatetimeInputFormats(languageTag?: Optional<string>): Array<string> {
  return [...getLocaleInputFormats(/* includeTime */ true, languageTag, true), ...DATETIME_FORMATS];
}

/**
 * Parse a free-text date/datetime while preserving an explicit UTC offset when the text carries one. Unlike
 * a plain moment() parse (which reinterprets the instant in the local timezone), an offset-bearing input is
 * re-parsed with parseZone so its authored offset survives. `hasOffset` reports whether an explicit offset
 * was present, since a Moment alone can't distinguish "no offset given" from "an explicit +00:00" — both
 * read back as utcOffset 0. Returns null if the text isn't a recognized date.
 *
 * `timezone` is an IANA zone name. This is the core parse used by every helper below; `_tryParseAsMoment`
 * is the same logic without the `hasOffset` flag.
 */
function _tryParseAsZonedMoment(
  text: string,
  languageTag?: Optional<string>,
  timezone?: Optional<string>
): {moment: moment.Moment; hasOffset: boolean} | null {
  const formats = localeDatetimeInputFormats(languageTag);
  const trimmed = text.trim();
  const local = moment(trimmed, formats, /* strict */ true);
  if (!local.isValid()) {
    return null;
  }
  const matchedFormat = local.creationData().format;
  const hasOffset = typeof matchedFormat === 'string' && matchedFormat.includes('Z');
  // If the input carries an explicit offset, preserve it via parseZone rather than reinterpreting it in
  // the local zone. If it has no offset and a valid timezone is passed in, interpret the wall-clock as
  // being in that IANA zone via moment.tz (DST-aware). Otherwise keep the plain parse (imposing a zone
  // would shift the displayed wall clock). moment.tz.zone returns null for an unknown name, so an
  // unrecognized timezone falls through to the plain parse instead of yielding an invalid moment — this
  // is a shared parser and not every caller validates the timezone.
  let parsedMoment: moment.Moment;
  if (hasOffset) {
    parsedMoment = moment.parseZone(trimmed, formats, /* strict */ true);
  } else if (timezone != null && moment.tz.zone(timezone) != null) {
    parsedMoment = moment.tz(trimmed, formats, /* strict */ true, timezone);
  } else {
    parsedMoment = local;
  }
  return {moment: parsedMoment, hasOffset};
}

function _tryParseAsMoment(
  text: string,
  languageTag?: Optional<string>,
  timezone?: Optional<string>
): moment.Moment | null {
  // With no snapshot timezone (feature off), match the pre-BNCH-155456 behavior exactly: a plain moment()
  // parse. This reinterprets the instant in the ambient zone and — crucially — returns a local-mode moment
  // even for offset-bearing input. Routing this case through _tryParseAsZonedMoment instead returned a
  // fixed-offset moment (moment.parseZone) whose spreadsheet serial number (momentToDaysSinceEpoch) is
  // DST-skewed, e.g. `=F7+(50/24)` rendered 01:00 instead of 02:00. The offset-preserving/zone-anchoring
  // path is used only when a timezone is supplied (feature on); refinements to it are handled separately.
  if (timezone == null) {
    const parsed = moment(text.trim(), localeDatetimeInputFormats(languageTag), /* strict */ true);
    return parsed.isValid() ? parsed : null;
  }
  return _tryParseAsZonedMoment(text, languageTag, timezone)?.moment ?? null;
}

export const tryParseAsZonedMoment = lruMemoize(
  _tryParseAsZonedMoment,
  {max: 2000},
  (text: string, languageTag?: Optional<string>, timezone?: Optional<string>) =>
    // Normalize timezone to null so undefined and null (which the parser treats identically via
    // `timezone != null`) share a cache entry rather than duplicating.
    `${text};${languageTag};${timezone ?? null}`
);

export const tryParseAsMoment = lruMemoize(
  _tryParseAsMoment,
  {max: 2000},
  (text: string, languageTag?: Optional<string>, timezone?: Optional<string>) =>
    // Normalize timezone to null so undefined and null (which the parser treats identically via
    // `timezone != null`) share a cache entry rather than duplicating.
    `${text};${languageTag};${timezone ?? null}`
);

function _tryParse(text: string, languageTag?: Optional<string>, timezone?: Optional<string>): string | null {
  const parsed = _tryParseAsMoment(text, languageTag, timezone);
  return parsed ? parsed.format(CANONICAL_FORMAT) : null;
}

export const tryParse = lruMemoize(
  _tryParse,
  {max: 2000},
  (text: string, languageTag?: Optional<string>, timezone?: Optional<string>) =>
    // Normalize timezone to null so undefined and null (which the parser treats identically via
    // `timezone != null`) share a cache entry rather than duplicating.
    `${text};${languageTag};${timezone ?? null}`
);

/**
 * @param {string} text: Representation of date-time value
 * @param {string} languageTag: Locale for date-time string value
 * @param {string} timezone: Document IANA timezone name used to anchor offset-less inputs (DST-aware)
 * @returns String representation for date value (with time value stripped)
 */
function _tryParseDatetimeAsDate(
  text: string,
  languageTag?: Optional<string>,
  timezone?: Optional<string>
): string | null {
  const parsed = _tryParseAsMoment(text, languageTag, timezone);
  return parsed ? parsed.format(USER_EDIT_DATE_FORMAT) : null;
}

export const tryParseDatetimeAsDate = lruMemoize(
  _tryParseDatetimeAsDate,
  {max: 2000},
  (text: string, languageTag?: Optional<string>, timezone?: Optional<string>) =>
    // Normalize timezone to null so undefined and null (which the parser treats identically via
    // `timezone != null`) share a cache entry rather than duplicating.
    `${text};${languageTag};${timezone ?? null}`
);

export function now(): string {
  return moment().format(USER_EDIT_DATETIME_FORMAT);
}
